/**
 * Runs the full text validation pipeline (steps 1–5). No API calls.
 * Pass optional config; if omitted, uses default (later replace with API config).
 */

import { normalizeText } from './normalizeText.js';
import { checkTextRules, checkReservedTerms, checkEmojiAndExcessiveSymbols } from './textRules.js';
import { checkGibberish } from './gibberish.js';
import { checkPolicyRules } from './policyRules.js';
import { getDefaultConfig } from './contentSafetyConfig.js';

/** @typedef {'name'|'businessName'|'message'|'review'|'quote'|'quoteMessage'|'support'|'supportSubject'|'post'|'description'|'jobTitle'|'jobDetails'|'report'|'location'|'topServices'|'portfolioTitle'} TextContext */

/**
 * Get limits for a context from config (for non-name fields).
 * @param {string} context
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} config
 */
function getLimitsForContext(context, config) {
  const limits = config.limits || {};
  switch (context) {
    case 'message': return limits.message;
    case 'jobTitle': return limits.jobTitle;
    case 'jobDetails': return limits.jobDetails;
    case 'quoteMessage': return limits.jobDetails;
    case 'review': return limits.reviewText;
    case 'post': return limits.postTitle;
    case 'portfolioTitle': return limits.portfolioTitle;
    case 'description': return limits.postDescription;
    case 'businessBio': return limits.businessBio;
    case 'customSubcategory': return limits.customSubcategory;
    case 'support': return limits.supportMessage;
    case 'supportSubject': return limits.supportSubject;
    case 'report': return limits.reportReason;
    case 'location': return limits.location;
    case 'topServices': return limits.topServices;
    default: return null;
  }
}

/**
 * Run the full pipeline. Returns { valid, errorCode, message, normalized }.
 * Use normalized value when submitting so backend receives clean text.
 *
 * @param {string} text - Raw input
 * @param {TextContext} context - Where this text is used
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} [config] - Optional; defaults to getDefaultConfig()
 * @returns {{ valid: boolean, errorCode?: string, message?: string, normalized?: string }}
 */
export function runTextPipeline(text, context, config) {
  const cfg = config || getDefaultConfig();
  const normalized = normalizeText(text);

  // Empty after normalise: let Yup/required handle "required"
  if (!normalized.length) {
    return { valid: true, normalized };
  }

  // Name contexts: strict rules + reserved only
  if (context === 'name' || context === 'businessName') {
    const result = checkTextRules(normalized, context, cfg);
    if (!result.valid) return { ...result, normalized };
    return { valid: true, normalized };
  }

  // Other contexts: length limits from config, then reserved, gibberish, policy
  const limits = getLimitsForContext(context, cfg);
  if (limits) {
    if (normalized.length < limits.min) {
      return { valid: false, errorCode: 'TOO_SHORT', message: cfg.messages.tooShort, normalized };
    }
    if (normalized.length > limits.max) {
      return { valid: false, errorCode: 'TOO_LONG', message: cfg.messages.tooLong, normalized };
    }
  }

  let result = checkReservedTerms(normalized, cfg);
  if (!result.valid) return { ...result, normalized };

  result = checkEmojiAndExcessiveSymbols(normalized, cfg);
  if (!result.valid) return { ...result, normalized };

  result = checkGibberish(normalized, cfg.gibberish, cfg.messages);
  if (!result.valid) return { ...result, normalized };

  result = checkPolicyRules(normalized, context, cfg);
  if (!result.valid) return { ...result, normalized };

  return { valid: true, normalized };
}

/**
 * Helper for Yup: use in .test() so validation uses current config.
 * Example: Yup.string().test('contentSafety', msg, (value) => runTextPipeline(value, 'name').valid !== false)
 *
 * @param {string} value
 * @param {TextContext} context
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} [config]
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateForForm(value, context, config) {
  const result = runTextPipeline(value, context, config);
  if (result.valid) return { valid: true };
  return { valid: false, message: result.message };
}
